import { isPast } from '@nucleus/lib-datetime';
import { isNotEmpty, slugifyString } from '@nucleus/lib-shape';
import { getKeyPrefix } from '@nucleus/ncu-key-prefix';
import {
  CollectionListController,
  CollectionListControllerOptions,
  PostAuthorProfile,
  PostAuthors,
  PostBlockController,
  PostDate,
  PostExpandedListItem,
  PostExpandedViewCard,
  PostGridItem,
  PostGridItemSkeleton,
  PostHubNav,
  PostListController,
  PostReadingTime,
  getCollectionTypeFromId,
  isDynamicPostListDataSource,
  isStaticPostDataSource,
  useAuthorPath,
  useCollection,
  useCollectionDataSourceParams,
  useCollectionListController,
  useCollectionPath,
  useCollectionTerm,
  useFirstValidAuthor,
  useLocalization,
  usePostBlockController,
  usePostBlockDataSourceCollectionListParams,
  usePostBlockDataSourcePostListParams,
  usePostHubPath,
  usePostListController,
} from '@nucleus/post-theme-elements';
import {
  Join,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemButton,
  Select,
  usePopoverState,
  useScrollNav,
} from '@nucleus/react-components';
import {
  MediaStrip as BaseMediaStrip,
  CircleProfileImage,
  CollectionList,
  CollectionListItem,
  CollectionListItemHeader,
  HorizontalRule,
  IconBible,
  IconPlaylist,
  IconTag,
  MediaExpandedList,
  MediaGrid,
  MediaGridItemBadge,
  MediaList,
  MediaListButton,
  MediaListItem,
  MediaListToolbar,
  SearchNavButton,
  ShareButton,
  ViewToggle,
} from '@nucleus/sermon-theme-elements';
import { PostBlockWeb, ThemeRichTextNodeArray } from '@nucleus/types/web';
import { AuthorWeb } from '@nucleus/types/web/posts/author';
import { PostIndexDocumentWeb } from '@nucleus/web-hosting';
import { useSection } from '@nucleus/web-theme';
import { Text, formatSrcSet, nucleusClass } from '@nucleus/web-theme-elements';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ButtonPrimary, ButtonSecondary, SectionButton } from '../components/Button';
import { IconAngleLeft, IconAngleRight } from '../components/Icons';
import { getWindow } from '../lib/dom';
import { BlockProps } from '../types/component';
import { BlockInfo } from './BlockInfo';
import { CollectionBlockSkeleton, PostBlockSkeleton } from './PostBlockSkeleton';

type BlockPostProps = Extract<BlockProps, { block?: PostBlockWeb; sectionType?: 'post' }>;

export const BlockPost = (props: BlockPostProps): JSX.Element => {
  return (
    <div className={classNames([props.className, nucleusClass('block-posts')])}>
      <PostBlockController {...props.block}>
        {props.block?.dataSource !== undefined && <SwitchBlockType {...props} />}
      </PostBlockController>
    </div>
  );
};

const SwitchBlockType = (props: BlockPostProps) => {
  switch (props.block?.dataSource?.itemType) {
    case 'postCollection':
    case 'author':
    case 'topic':
      if (
        props.block?.dataSource?.type === 'dynamic' &&
        props.block?.items !== undefined &&
        props.block?.items.length > 0
      ) {
        return <PostsBlock {...props} />;
      }
      return <CollectionsBlock {...props} />;
    case 'post':
    case undefined:
    default:
      return <PostsBlock {...props} />;
  }
};

const CollectionsBlock = (props: BlockPostProps) => {
  return (
    <React.Suspense
      fallback={
        <ColumnDiv>
          <CollectionBlockSkeleton />
        </ColumnDiv>
      }
    >
      <CollectionListController
        engineId={props.block?.engineId}
        basePath={usePostHubPath(props.block?.engineId)}
        {...usePostBlockDataSourceCollectionListParams(props.block)}
      >
        <ColumnDiv>
          <CollectionsListLayout {...props} />
        </ColumnDiv>
      </CollectionListController>
    </React.Suspense>
  );
};

const CollectionsListLayout = (props: BlockPostProps) => {
  const { collections, collectionType, hasNextPage, fetchNextPage, isFetchingNextPage } = useCollectionListController();

  const enabledLoadMore = props.block?.dataSource?.isLoadMoreEnabled !== false;

  return (
    <>
      <BlockTitle title={props.block?.title} />
      {props.block?.isPostHubNavVisible && <CollectionListToolbar />}
      {collections.length > 0 ? (
        <>
          <StyledCollectionList className={nucleusClass('collection-list')}>
            {collections.map((collection: { id: string; slug: string }) => (
              <React.Suspense key={collection.id} fallback={<PostGridItemSkeleton />}>
                <PostListCollectionItem engineId={props.block?.engineId} collection={collection} />
              </React.Suspense>
            ))}
          </StyledCollectionList>
          {enabledLoadMore && hasNextPage && (
            <SectionButton
              style={{ width: '100%' }}
              onClick={fetchNextPage}
              loading={isFetchingNextPage}
              disabled={isFetchingNextPage}
            >
              Load More
            </SectionButton>
          )}
        </>
      ) : (
        <EmptyBlock body={getEmptyCollectionListBody(collectionType)} />
      )}
    </>
  );
};

interface PostListCollectionItemProps {
  engineId?: string;
  collection: { id: string; slug: string };
}

const PostListCollectionItem = ({ collection, engineId }: PostListCollectionItemProps) => {
  return (
    <PostListController
      engineId={engineId ?? ''}
      basePath={usePostHubPath(engineId)}
      params={useCollectionDataSourceParams(collection.id)}
    >
      <CollectionItem collection={collection} />
    </PostListController>
  );
};

const getEmptyCollectionListBody = (
  collectionType?: 'postCollections' | 'tags' | 'authors'
): ThemeRichTextNodeArray => {
  switch (collectionType) {
    case 'postCollections':
      return [
        { text: `Items show here when they've been added to a ` },
        { type: 'parameter', parameter: 'language:postCollection', fallback: '', children: [{ text: '' }] },
      ];
    case 'authors':
      return [
        { text: `Items show here when they're associated with an ` },
        { type: 'parameter', parameter: 'language:author', fallback: '', children: [{ text: '' }] },
      ];
    case 'tags':
      return [
        { type: 'parameter', parameter: 'language:topics', fallback: '', children: [{ text: '' }] },
        { text: ` show here when they're associated with a post` },
      ];
    default:
      return [{ text: '' }];
  }
};

interface CollectionItemProps {
  collection: { id: string; title?: string; label?: string; headshot?: { image?: any }; displayName?: string };
}

const CollectionItem = (props: CollectionItemProps) => {
  const { posts, getPostPath } = usePostListController();
  const { isImageVisible } = usePostBlockController();
  const { ref, prev, next } = useScrollNav();

  return (
    <CollectionListItem className={nucleusClass('collection-item')}>
      {getKeyPrefix(props.collection.id) === 'author' ? (
        <AuthorItemHeader {...props} />
      ) : (
        <PostCollectionItemHeader {...props} />
      )}
      <MediaStrip className={nucleusClass('post-filmstrip')} flexBasis="min(100%, 340px)" ref={ref}>
        {posts.map((post) => (
          <PostGridItem
            key={post.id}
            className={nucleusClass('post-item')}
            image={post.mainImage?.image}
            isImageVisible={isImageVisible}
            hoverButton={<ButtonPrimary>Read Post</ButtonPrimary>}
            to={getPostPath(post)}
          >
            <PostGridItemContent post={post} getPostPath={getPostPath} lineClamp={2} />
          </PostGridItem>
        ))}
      </MediaStrip>
      <ButtonGroup>
        <PrevButton onClick={prev} />
        <NextButton onClick={next} />
      </ButtonGroup>
    </CollectionListItem>
  );
};

const PostCollectionItemHeader = (props: CollectionItemProps) => {
  const { getCollectionPath, collectionType } = useCollectionListController();
  return (
    <CollectionListItemHeader
      left={<CollectionIcon type={collectionType} height="30px" width="30px" />}
      right={<ViewCollectionButton {...props} />}
    >
      <ClampedH5 style={{ margin: 0 }}>
        <Link style={{ textDecoration: 'none' }} to={getCollectionPath(props.collection)}>
          {props.collection.title ?? props.collection.label}
        </Link>
      </ClampedH5>
    </CollectionListItemHeader>
  );
};

const AuthorItemHeader = (props: CollectionItemProps) => {
  const { getCollectionPath } = useCollectionListController();
  const profileImage = props.collection.headshot?.image;

  return (
    <CollectionListItemHeader
      left={
        <>
          {profileImage?.src && (
            <CircleProfileImage
              style={{ height: '50px', width: '50px' }}
              src={profileImage?.src}
              srcSet={formatSrcSet(profileImage?.srcSet)}
              blurHash={profileImage?.blurHash}
              focalPoint={profileImage?.focalPoint}
            />
          )}
        </>
      }
      right={<ViewCollectionButton {...props} />}
    >
      <ClampedL2 style={{ margin: 0 }}>
        <Link style={{ textDecoration: 'none' }} to={getCollectionPath(props.collection)}>
          {props.collection.displayName}
        </Link>
        <ClampedL6>{props.collection.title}</ClampedL6>
      </ClampedL2>
    </CollectionListItemHeader>
  );
};

const ViewCollectionButton = (props: CollectionItemProps) => {
  const { getCollectionPath } = useCollectionListController();
  return (
    <MediaListButton to={getCollectionPath(props.collection)}>
      View {useCollectionTerm(props.collection)}
    </MediaListButton>
  );
};

const CollectionIcon = ({
  type,
  ...props
}: { type: CollectionListControllerOptions['collection'] } & React.SVGAttributes<SVGSVGElement>) => {
  switch (type) {
    case 'tags':
      return <StyledIconTag {...props} />;
    case 'postCollections':
    default:
      return <StyledIconPlaylist {...props} />;
  }
};

const StyledIconPlaylist = styled(IconPlaylist)`
  display: block;
`;

const StyledIconBible = styled(IconBible)`
  display: block;
`;

const StyledIconTag = styled(IconTag)`
  display: block;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 30px;
`;

function withIcon<P>(Component: React.FunctionComponent<P>, icon: JSX.Element) {
  return function WithIcon(props: P) {
    return <Component shape="square" size="medium" icon={icon} {...props} />;
  };
}

const PrevButton = withIcon(ButtonSecondary, <IconAngleLeft />);
const NextButton = withIcon(ButtonPrimary, <IconAngleRight />);

const PostsBlock = (props: BlockPostProps) => {
  const { currentLayout } = usePostBlockController();

  const dataSource = props.block?.dataSource;
  const skeletonCount = dataSource?.limit ?? 6;

  // FIXME: This hack produces a boolean in some cases. This should be refactored.
  const currentlyPlayingPostId =
    (isDynamicPostListDataSource(dataSource) || isStaticPostDataSource(dataSource)) &&
    dataSource?.currentlyPlayingPostId;

  return (
    <React.Suspense
      fallback={
        <ColumnDiv>
          <PostBlockSkeleton layout={currentLayout} count={skeletonCount} />
        </ColumnDiv>
      }
    >
      <PostListController
        engineId={props.block?.engineId}
        basePath={usePostHubPath(props.block?.engineId)}
        currentlyPlayingPostId={currentlyPlayingPostId}
        params={usePostBlockDataSourcePostListParams(props.block)}
      >
        <ColumnDiv>
          <SwitchPostListLayout {...props} />
        </ColumnDiv>
      </PostListController>
    </React.Suspense>
  );
};

const SwitchPostListLayout = (props: BlockPostProps) => {
  const { currentLayout } = usePostBlockController();

  switch (currentLayout) {
    case 'expanded':
      return <ExpandedView {...props} />;
    case 'filmstrip':
      return <FilmstripView {...props} />;
    case 'list':
      return <ListView {...props} />;
    case 'grid':
    default:
      return <GridView {...props} />;
  }
};

const ExpandedView = (props: BlockPostProps) => {
  const { posts, getPostPath, hasNextPage, fetchNextPage, isFetchingNextPage } = usePostListController();
  const { isImageVisible } = usePostBlockController();
  const localize = useLocalization();

  const enabledLoadMore = props.block?.dataSource?.isLoadMoreEnabled !== false;

  return (
    <ExpandedViewContainer>
      <PostExpandedViewCard>
        <BlockTitle title={props.block?.title} />
        <MediaExpandedList className={nucleusClass('post-expanded-list')}>
          {posts.map((post, index) => (
            <PostExpandedListItem
              key={post.id}
              className={nucleusClass('post-item')}
              image={post.mainImage?.image}
              isImageVisible={isImageVisible}
              to={getPostPath(post)}
            >
              {index === 0 && props.block?.items?.[0]?.overline && (
                <ClampedL5>
                  {localize(
                    (props.block?.items?.[0]?.overline ?? '').toLocaleLowerCase(),
                    props.block?.items?.[0]?.overline ?? ''
                  )}
                </ClampedL5>
              )}
              <ClampedH5
                style={{ maxWidth: '95%', marginTop: props.block?.items?.[0]?.overline ? '0.6em' : undefined }}
              >
                <LineClamp style={{ lineHeight: '1em', paddingBottom: '0.1em' }} lines={2}>
                  {post.title}
                </LineClamp>
              </ClampedH5>
              <ClampedP4 style={{ maxWidth: '90%', marginTop: '0.75em' }}>
                <LineClamp lines={3}>{post.bodyText}</LineClamp>
              </ClampedP4>
              <ClampedL6 style={{ maxWidth: '85%', marginTop: '1.25em' }}>
                <AuthorByline post={post} />
              </ClampedL6>
              <div style={{ maxWidth: '216px', marginTop: '1.8em' }}>
                <ButtonPrimary iconRight={<IconAngleRight />} to={getPostPath(post)}>
                  Read Post
                </ButtonPrimary>
              </div>
            </PostExpandedListItem>
          ))}
        </MediaExpandedList>
        {enabledLoadMore && hasNextPage && (
          <SectionButton
            style={{ width: '100%' }}
            onClick={fetchNextPage}
            loading={isFetchingNextPage}
            disabled={isFetchingNextPage}
          >
            Load More
          </SectionButton>
        )}
      </PostExpandedViewCard>
    </ExpandedViewContainer>
  );
};

const ExpandedViewContainer = styled.div`
  @media (min-width: 600px) {
    padding: 0 calc(var(--unit-length) * 1);
  }
`;

const ColumnDiv = styled.div`
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
`;

const FilmstripView = (props: BlockPostProps) => {
  const { currentlyPlayingPost, posts, getPostPath } = usePostListController();
  const { isImageVisible } = usePostBlockController();
  const { ref, prev, next } = useScrollNav();

  const currentlyPlayingIndex = posts.findIndex((post) => post.id === currentlyPlayingPost?.id);

  useScrollToCurrentlyPlaying(ref, currentlyPlayingIndex);

  return (
    <>
      <BlockTitle
        title={props.block?.title}
        subtitle={(() => {
          const dataSource = props.block?.dataSource;
          return isDynamicPostListDataSource(dataSource) && dataSource?.sourceId !== undefined ? (
            <FilmstripSubtitle {...props} />
          ) : undefined;
        })()}
      />
      <div>
        <MediaStrip
          className={nucleusClass('post-filmstrip')}
          flexBasis="min(100%, max(calc(33% - 6px), 300px))"
          ref={ref}
        >
          {posts.map((post, index) => (
            <PostGridItem
              key={post.id}
              className={nucleusClass('post-item')}
              isCurrentlyReading={index === currentlyPlayingIndex}
              isImageVisible={isImageVisible}
              image={post.mainImage?.image}
              hoverButton={<ButtonPrimary>Read Post</ButtonPrimary>}
              currentlyReadingButton={<MediaGridItemBadge>Currently Reading</MediaGridItemBadge>}
              to={getPostPath(post)}
            >
              <PostGridItemContent post={post} getPostPath={getPostPath} lineClamp={2} />
            </PostGridItem>
          ))}
        </MediaStrip>
        <ButtonGroup>
          <PrevButton onClick={prev} />
          <NextButton onClick={next} />
        </ButtonGroup>
      </div>
    </>
  );
};

const useScrollToCurrentlyPlaying = (ref: React.RefObject<HTMLDivElement>, currentIndex: number) => {
  useEffect(() => {
    if (!ref.current || currentIndex < 0) {
      return;
    }

    const nextIndex = Math.min(Math.max(0, currentIndex - 1), ref.current.children.length);

    const nextItem = ref.current.children[nextIndex] as HTMLElement | undefined;

    if (!nextItem) {
      return;
    }

    ref.current.scrollTo({
      left: nextItem.offsetLeft,
    });
  }, [currentIndex, ref]);
};

const FilmstripSubtitle = (props: BlockPostProps) => {
  const collectionType = getCollectionTypeFromId(props.block?.dataSource?.sourceId)!;
  const { data } = useCollection(props.block?.engineId, `${collectionType}s`, props.block?.dataSource?.sourceId);
  const getCollectionPath = useCollectionPath(usePostHubPath(props.block?.engineId));

  if (data.collection === undefined) {
    return null;
  }

  const PrefixTermMap: Record<string, string> = {
    postCollection: 'Collection',
    tag: 'Tag',
    author: 'Author',
  };

  const term = PrefixTermMap[collectionType];
  const title = [data?.collection?.displayName, data?.collection?.title].filter(isNotEmpty).at(0);

  return (
    <>
      <span style={{ opacity: 0.65 }}>From this {term}: </span>
      <Link to={getCollectionPath(data?.collection)}>{title}</Link>
    </>
  );
};

const ListView = (props: BlockPostProps) => {
  const { posts, getPostPath, hasNextPage, fetchNextPage, isFetchingNextPage } = usePostListController();

  const renderListNavToolbar = props.block?.isPostHubNavVisible === true;
  const renderListViewToolbar = renderListNavToolbar !== true && props.block?.isPostListNavVisible !== false;

  const renderLoadMore = props.block?.dataSource?.isLoadMoreEnabled !== false && hasNextPage === true;

  return (
    <>
      <BlockTitle title={props.block?.title} />
      {renderListNavToolbar && <PostListNavToolbar />}
      {renderListViewToolbar && <PostListViewToolbar currentLayout="list" />}
      <MediaList className={nucleusClass('post-list')}>
        {posts.map((post) => (
          <ListViewItem key={post.id} post={post} getPostPath={getPostPath} />
        ))}
      </MediaList>
      {renderLoadMore && (
        <SectionButton
          style={{ width: '100%' }}
          onClick={fetchNextPage}
          loading={isFetchingNextPage}
          disabled={isFetchingNextPage}
        >
          Load More
        </SectionButton>
      )}
    </>
  );
};

interface ListViewItemProps {
  post: PostIndexDocumentWeb;
  getPostPath: (post: PostIndexDocumentWeb) => string;
}

export const ListViewItem = ({ post, getPostPath }: ListViewItemProps): JSX.Element => {
  const { isImageVisible } = usePostBlockController();

  return (
    <StyledMediaListItem
      key={post.id}
      className={nucleusClass('post-item')}
      actions={<ActionsMenu to={getPostPath(post)} />}
      image={post.mainImage?.image}
      isImageVisible={isImageVisible}
      to={getPostPath(post)}
    >
      <ListItemOverline style={{ marginBottom: '1em' }}>
        <DateByline post={post} />
      </ListItemOverline>
      <ListItemHeadline>{post.title}</ListItemHeadline>
      <div style={{ maxWidth: '60%' }}>
        <ClampedP4>
          <LineClamp lines={3}>{post.bodyText}</LineClamp>
        </ClampedP4>
      </div>
    </StyledMediaListItem>
  );
};

const StyledMediaListItem = styled(MediaListItem)`
  align-items: flex-start;
  justify-content: flex-start;

  @media (max-width: 800px) {
    & > div:last-child {
      margin-left: 1rem;
      padding-bottom: 1rem;
    }
  }
`;

const GridView = (props: BlockPostProps) => {
  const { posts, getPostPath, hasNextPage, fetchNextPage, isFetchingNextPage } = usePostListController();

  const renderListNav = props.block?.isPostHubNavVisible === true;
  const renderListView = renderListNav !== true && props.block?.isPostListNavVisible !== false;

  const renderLoadMore = props.block?.dataSource?.isLoadMoreEnabled !== false && hasNextPage === true;

  return (
    <>
      <BlockTitle title={props.block?.title} />
      {renderListNav && <PostListNavToolbar />}
      {renderListView && <PostListViewToolbar currentLayout="grid" />}
      <PostGrid className={nucleusClass('post-grid')}>
        {posts.map((post) => (
          <GridViewItem key={post.id} post={post} getPostPath={getPostPath} />
        ))}
      </PostGrid>
      {renderLoadMore && (
        <SectionButton
          style={{ width: '100%' }}
          onClick={fetchNextPage}
          loading={isFetchingNextPage}
          disabled={isFetchingNextPage}
        >
          Load More
        </SectionButton>
      )}
    </>
  );
};

const PostGrid = styled(MediaGrid)`
  --min-item-size: 300px;
  --row-gap: 18px;
  margin-bottom: 36px;
`;

interface GridViewItemProps {
  post: PostIndexDocumentWeb;
  getPostPath: (post: PostIndexDocumentWeb) => string;
}

export const GridViewItem = (props: GridViewItemProps): JSX.Element => {
  const { isImageVisible } = usePostBlockController();

  return (
    <PostGridItem
      className={nucleusClass('post-item')}
      image={props.post.mainImage?.image}
      isImageVisible={isImageVisible}
      hoverButton={<ButtonPrimary>Read Post</ButtonPrimary>}
      to={props.getPostPath(props.post)}
    >
      <PostGridItemContent post={props.post} getPostPath={props.getPostPath} />
    </PostGridItem>
  );
};

interface PostGridItemContentProps {
  post: PostIndexDocumentWeb;
  getPostPath: (post: PostIndexDocumentWeb) => string;
  lineClamp?: number;
}

const PostGridItemContent = ({ post, getPostPath, lineClamp }: PostGridItemContentProps): JSX.Element => {
  return (
    <>
      <GridItemOverline>
        <DateByline post={post} />
      </GridItemOverline>
      <GridItemTitle>
        <LineClamp style={{ lineHeight: '1em', paddingBottom: '0.1em' }} lines={3}>
          {post.title}
        </LineClamp>
      </GridItemTitle>
      <div style={{ maxWidth: '80%' }}>
        <GridItemBody>
          <LineClamp lines={lineClamp ?? 3}>{post.bodyText}</LineClamp>
        </GridItemBody>
      </div>
      <PostItemFooterRow>
        <PostItemAuthorProfile post={post} />
        <div onClick={(event) => event.stopPropagation()}>
          <ActionsMenu title={post.title ?? post.slug} to={getPostPath(post)} />
        </div>
      </PostItemFooterRow>
    </>
  );
};

const BlockTitle = ({ title, subtitle }: { title?: React.ReactNode; subtitle?: React.ReactNode }) => {
  const localize = useLocalization();

  if (title === undefined) {
    return null;
  }

  if (typeof title === 'string') {
    title = localize(title.toLocaleLowerCase(), title);
  }

  if (subtitle !== undefined) {
    return (
      <div style={{ marginBottom: '24px' }}>
        <HorizontalRule>
          <ClampedH5 style={{ margin: 0 }}>{title}</ClampedH5>
        </HorizontalRule>
        <ClampedL2 style={{ margin: '6px 0 0 0' }}>{subtitle}</ClampedL2>
      </div>
    );
  }

  return (
    <HorizontalRule style={{ marginBottom: '24px', marginLeft: '24px', marginRight: '24px' }}>
      <ClampedH5 style={{ margin: 0 }}>{title}</ClampedH5>
    </HorizontalRule>
  );
};

const EmptyBlock = ({
  headline = [{ text: 'Nothing here right now…' }],
  body = [{ text: `Items shows here when they've been added to a Collection` }],
}: {
  headline?: ThemeRichTextNodeArray;
  body?: ThemeRichTextNodeArray;
}) => {
  return (
    <BlockInfo
      blockHeadlineMaxWidth="small"
      block={{
        headline: [
          {
            type: 'heading-six',
            fontStyle: 'headline6',
            children: headline,
          },
        ],
        body: [
          {
            type: 'paragraph',
            fontStyle: 'paragraph3',
            children: body,
          },
        ],
      }}
    />
  );
};

const ActionsMenu = (props: { title?: string; to: string }) => {
  const { popoverProps, triggerProps } = usePopoverState();

  const url = `${getWindow()?.location.origin}${props.to}`;

  return (
    <>
      <MenuButton {...triggerProps} />
      <Menu {...popoverProps}>
        <ViewItem to={props.to}>Read Post</ViewItem>
        <CopyLinkItem url={url}>Copy Link</CopyLinkItem>
        <ShareItem title={props.title ?? ''} url={url}>
          Sharing Options
        </ShareItem>
      </Menu>
    </>
  );
};

export const PostBlockShareMenu = (): JSX.Element => {
  const { popoverProps, triggerProps } = usePopoverState();

  const shareUrl = `${getWindow()?.location.href}#${useSection().id}`;

  return (
    <>
      <ShareButton {...triggerProps} />
      <Menu {...popoverProps}>
        <CopyLinkItem url={shareUrl}>Copy Link</CopyLinkItem>
        <ShareItem title={document.title ?? ''} url={shareUrl}>
          Sharing Options
        </ShareItem>
      </Menu>
    </>
  );
};

const ViewItem = (props: { children?: React.ReactNode; to: string }) => {
  return (
    <MenuItem>
      <Link to={props.to} style={{ textDecoration: 'none' }}>
        <MenuItemButton>{props.children}</MenuItemButton>
      </Link>
    </MenuItem>
  );
};

const CopyLinkItem = (props: { children?: React.ReactNode; url: string }) => {
  const [isCopied, setIsCopied] = React.useState(false);

  const handleCopyLink = () => {
    copy(props.url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1000);
  };

  return (
    <MenuItem>
      <MenuItemButton onClick={handleCopyLink}>{isCopied === true ? 'Copied!' : props.children}</MenuItemButton>
    </MenuItem>
  );
};

const ShareItem = (props: { children?: React.ReactNode; title: string; url: string }) => {
  const handleShareSheet = () => {
    if (navigator.share) {
      navigator.share({
        text: props.title,
        url: props.url,
      });
    }
  };

  if (navigator.share === undefined) {
    return null;
  }

  return (
    <MenuItem>
      <MenuItemButton onClick={handleShareSheet}>{props.children}</MenuItemButton>
    </MenuItem>
  );
};

const SortSelect = styled((props) => {
  const { availableToOrderBy, order, setOrder, orderBy, setOrderBy } = usePostListController();

  const value = [orderBy, order].join(':');

  const handleChange = (value: string) => {
    const [orderBy, order] = value.split(':') as [string, 'asc' | 'desc'];
    setOrderBy(orderBy);
    setOrder(order);
  };

  const isAvailable = (key: string) => availableToOrderBy.includes(key as any);

  if (availableToOrderBy.length < 1) {
    return null;
  }

  return (
    <Select {...props} label="Sort" value={value} onChange={handleChange}>
      {isAvailable('postCollection') && <option value="postCollection:desc">Collection</option>}
      {isAvailable('date') && <option value="date:desc">Latest</option>}
      {isAvailable('date') && <option value="date:asc">Oldest</option>}
    </Select>
  );
})``;

type ToolbarProps = Parameters<typeof MediaListToolbar>[0];

const PostListNavToolbar = styled((props: ToolbarProps) => {
  const localize = useLocalization();

  return (
    <MediaListToolbar
      {...props}
      left={<PostHubNav />}
      right={
        <>
          <SortSelect />
          <SearchNavButton to={slugifyString(localize('search', 'search'))} />
        </>
      }
    />
  );
})`
  ${SortSelect} .SelectValue {
    display: none;
    @media screen and (min-width: 600px) {
      display: initial;
    }
  }
`;

const PostListViewToolbar = styled((props: ToolbarProps & { currentLayout: 'grid' | 'list' }) => {
  const { onLayoutChange } = usePostBlockController();

  return (
    <MediaListToolbar
      {...props}
      left={
        <>
          <ViewToggle value={props.currentLayout} onChange={onLayoutChange} />
          <SortSelect />
        </>
      }
      right={
        <>
          <PostBlockShareMenu />
        </>
      }
    />
  );
})``;

const CollectionListToolbar = () => {
  return <MediaListToolbar left={<PostHubNav />} right={<SearchNavButton to="search" />} />;
};

interface BylineProps {
  post: PostIndexDocumentWeb;
}

export const AuthorByline = (props: BylineProps): JSX.Element => {
  return (
    <Join separator=" • ">
      <PostAuthors post={props.post} />
      <PostReadingTime post={props.post} />
    </Join>
  );
};

export const DateByline = (props: BylineProps): JSX.Element => {
  return (
    <Join separator=" • ">
      <PostDate post={props.post} />
      <PostReadingTime post={props.post} />
    </Join>
  );
};

interface PostItemAuthorProfileProps {
  post: PostIndexDocumentWeb;
}

const PostItemAuthorProfile = ({ post }: PostItemAuthorProfileProps): JSX.Element | null => {
  const basePath = usePostHubPath(post.engineId);
  const toAuthor = useAuthorPath(basePath);
  const author: AuthorWeb | undefined = useFirstValidAuthor(post.engineId, post.authorIds ?? []);

  if (author === undefined) {
    return <div />;
  }

  const headshot = author.headshot?.image;
  const isArchived = isPast(author.archivedAt);

  return (
    <PostAuthorProfile
      to={isArchived ? undefined : toAuthor(author)}
      src={headshot?.src}
      srcSet={formatSrcSet(headshot?.srcSet)}
      blurHash={headshot?.blurHash}
      focalPoint={headshot?.focalPoint}
    >
      <AuthorProfileName>{author.displayName}</AuthorProfileName>
      <AuthorProfileTitle>{author.title}</AuthorProfileTitle>
    </PostAuthorProfile>
  );
};

const PostItemFooterRow = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const AuthorProfileName = styled.p`
  margin: 0;
  font-size: clamp(10px, 3.37cqi, 14px);
  font-weight: 500;
  letter-spacing: calc(-0.5 / 12 * 1em);
  line-height: 1em;
`;

const AuthorProfileTitle = styled.p`
  margin: 0;
  font-size: clamp(7px, 2.25cqi, 10px);
  letter-spacing: calc(-0.25 / 8 * 1em);
  line-height: 1em;
`;

const clampFontFactory = (min: string, cssVariable: string, max: string) =>
  `clamp(${min}, var(${cssVariable}) * var(--global-font-factor), ${max})`;

const GridItemTitle = styled.div.attrs({ className: 'theme-text-label1' })`
  && {
    font-size: clamp(22px, 6.48cqi, 30px);
    margin-bottom: 0.2em;
  }
  width: 90%;
`;

const ClampedH5 = styled(Text.H5)`
  font-size: ${clampFontFactory('30px', '--headline5-font-size', '46px')};
`;

const ClampedH6 = styled(Text.H6)`
  font-size: ${clampFontFactory('26px', '--headline6-font-size', '32px')};
`;

const ClampedL2 = styled(Text.L2)`
  font-size: ${clampFontFactory('19px', '--label2-font-size', '29px')};
`;

const ClampedL5 = styled(Text.L5)`
  font-size: ${clampFontFactory('12px', '--label5-font-size', '19px')};
`;

const ClampedL6 = styled(Text.L6)`
  font-size: ${clampFontFactory('12px', '--label6-font-size', '19px')};
  && {
    margin: 0;
  }
`;

const ListItemOverline = styled(Text.L6)`
  font-size: ${clampFontFactory('8px', '--label6-font-size', '12px')};
  letter-spacing: calc(1.1 / 10 * 1em);
  text-transform: uppercase;
  font-weight: 600;
  opacity: 0.5;
`;

const ListItemHeadline = styled(ClampedH6)`
  margin-top: 0px;
  margin-bottom: 0.25em;
  max-width: 89%;
`;

const GridItemOverline = styled(Text.L6)`
  font-size: clamp(12px, 3cqi, 19px);
  font-weight: 500;
  opacity: 0.5;
`;

const GridItemBody = styled(Text.L6)`
  font-size: clamp(12px, 4.5cqi, 19px);
  line-height: 1.25em;
  margin: 0;
`;

const ClampedP4 = styled(Text.P4)`
  font-size: ${clampFontFactory('12px', '--paragraph4-font-size', '19px')};
`;

interface LineClampProps {
  lines: number;
}

const LineClamp = styled.span<LineClampProps>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ lines }) => lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledCollectionList = styled(CollectionList)`
  .nucleus-post-item {
    background: var(--color-block-background);
  }
`;

const MediaStrip = styled(BaseMediaStrip)`
  padding-block: 36px;
  margin-block: -36px;

  .nucleus-post-item {
    transition:
      background 250ms cubic-bezier(0.23, 1, 0.32, 1),
      box-shadow 250ms cubic-bezier(0.23, 1, 0.32, 1);

    &:hover {
      background: ${({ theme }) => theme._postThemeElements.PostGridItem?.hover?.background};
      box-shadow: ${({ theme }) => theme._postThemeElements.PostGridItem?.hover?.shadow};
    }
  }
`;
